import { useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { useEntityMachine } from '@/hooks/useEntityMachine';
import { usePermissions } from '@/hooks/usePermissions';
import { DynamicForm } from './DynamicForm';
import { EntityActivityLog } from './EntityActivityLog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, RefreshCw, MoreHorizontal, Pencil, Trash2, Eye, History, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { UniversalFilters, FilterFieldConfig } from '@/components/filters/UniversalFilters';
import { FilterValue } from '@/hooks/useSavedFilters';
import { supabase } from '@/integrations/supabase/client';

interface EntityManagerProps {
  entityName: string;
  headerActions?: ReactNode;
  onRowClick?: (item: any) => void;
  advancedFilterFields?: FilterFieldConfig[];
}

export function EntityManager({
  entityName,
  headerActions,
  onRowClick,
  advancedFilterFields,
}: EntityManagerProps) {
  const {
    items,
    loading,
    config,
    pagination,
    filters,
    advancedFilters,
    searchQuery,
    fetch,
    refresh,
    create,
    update,
    remove,
    bulkDelete,
    setFilters,
    setAdvancedFilters,
    setSearch,
    setPage,
  } = useEntityMachine(entityName);
  
  const { can } = usePermissions();
  
  // Local state
  const [formOpen, setFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchQuery) {
        setSearch(localSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, searchQuery, setSearch]);

  // Handle advanced filter changes
  const handleAdvancedFiltersChange = useCallback((filterValues: FilterValue[]) => {
    setAdvancedFilters(filterValues);
  }, [setAdvancedFilters]);

  const features = config.features || {};
  const canCreate = features.canCreate !== false && can.create(config.permissionModule);
  const canUpdate = features.canUpdate !== false && can.update(config.permissionModule);
  const canDelete = features.canDelete !== false && can.delete(config.permissionModule);
  const canBulkDelete = features.canBulkDelete !== false && can.delete(config.permissionModule);

  // Handlers
  const handleCreate = () => {
    setSelectedItem(null);
    setFormOpen(true);
  };

  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormOpen(true);
  };

  const handleView = (item: any) => {
    setSelectedItem(item);
    setDetailOpen(true);
  };

  const handleDeleteClick = (item: any) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      await remove(itemToDelete[config.keyField]);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    const ids = Array.from(selectedItems);
    await bulkDelete(ids);
    setSelectedItems(new Set());
    setBulkDeleteDialogOpen(false);
  };

  const handleFormSubmit = async (data: Record<string, any>) => {
    if (selectedItem) {
      await update(selectedItem[config.keyField], data);
    } else {
      await create(data);
    }
    setFormOpen(false);
    setSelectedItem(null);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(items.map((item: any) => item[config.keyField])));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleFilterChange = (filterId: string, value: string) => {
    const filterConfig = config.filters?.find(f => f.id === filterId);
    if (filterConfig) {
      setFilters({ ...filters, [filterConfig.field]: value });
    }
  };

  // Render helpers
  const renderCell = (item: any, column: any) => {
    if (column.cell) {
      return column.cell(item);
    }
    if (column.accessorFn) {
      return column.accessorFn(item);
    }
    if (column.accessorKey) {
      return item[column.accessorKey] ?? '-';
    }
    return '-';
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);
  const visibleColumns = config.columns.filter(col => col.defaultVisible !== false);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{config.pluralName}</h1>
          {config.description && (
            <p className="text-muted-foreground">{config.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          {canCreate && (
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add {config.singularName}
            </Button>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          {features.canSearch !== false && (
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${config.pluralName.toLowerCase()}...`}
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          )}
          
          {config.filters?.map(filter => (
            <Select
              key={filter.id}
              value={filters[filter.field] || filter.defaultValue || 'all'}
              onValueChange={(value) => handleFilterChange(filter.id, value)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={filter.label} />
              </SelectTrigger>
              <SelectContent>
                {filter.options?.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}

          {advancedFilterFields && advancedFilterFields.length > 0 && (
            <Button 
              variant={showAdvancedFilters ? 'secondary' : 'outline'} 
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Advanced Filters
              {advancedFilters.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {advancedFilters.length}
                </Badge>
              )}
            </Button>
          )}

          <Button variant="outline" size="icon" onClick={refresh}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>

          {canBulkDelete && selectedItems.size > 0 && (
            <Button variant="destructive" onClick={handleBulkDeleteClick}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedItems.size})
            </Button>
          )}
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && advancedFilterFields && advancedFilterFields.length > 0 && (
          <UniversalFilters
            entityType={config.tableName}
            fields={advancedFilterFields}
            onFiltersChange={handleAdvancedFiltersChange}
          />
        )}
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              {canBulkDelete && (
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedItems.size === items.length && items.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
              )}
              {visibleColumns.map(column => (
                <TableHead key={column.id} style={{ width: column.width }}>
                  {column.header}
                </TableHead>
              ))}
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && items.length === 0 ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {canBulkDelete && <TableCell><Skeleton className="h-4 w-4" /></TableCell>}
                  {visibleColumns.map(col => (
                    <TableCell key={col.id}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                  <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={visibleColumns.length + (canBulkDelete ? 2 : 1)} 
                  className="text-center py-8 text-muted-foreground"
                >
                  <div className="flex flex-col items-center gap-2">
                    {config.icon}
                    <p>No {config.pluralName.toLowerCase()} found</p>
                    {canCreate && (
                      <Button variant="outline" size="sm" onClick={handleCreate}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add {config.singularName}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item: any) => (
                <TableRow 
                  key={item[config.keyField]}
                  className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
                  onClick={() => onRowClick?.(item)}
                >
                  {canBulkDelete && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedItems.has(item[config.keyField])}
                        onCheckedChange={(checked) => handleSelectItem(item[config.keyField], !!checked)}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map(column => (
                    <TableCell key={column.id}>
                      {renderCell(item, column)}
                    </TableCell>
                  ))}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleView(item)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </DropdownMenuItem>
                        {canUpdate && (
                          <DropdownMenuItem onClick={() => handleEdit(item)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick(item)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {pagination.total > pagination.pageSize && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
              {pagination.total} results
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(pagination.page - 1)}
                disabled={pagination.page === 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Page {pagination.page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(pagination.page + 1)}
                disabled={pagination.page >= totalPages || loading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedItem ? `Edit ${config.singularName}` : `Create ${config.singularName}`}
            </DialogTitle>
            <DialogDescription>
              {selectedItem 
                ? `Update the ${config.singularName.toLowerCase()} details below.`
                : `Fill in the details to create a new ${config.singularName.toLowerCase()}.`
              }
            </DialogDescription>
          </DialogHeader>
          <DynamicForm
            fields={config.formFields}
            initialValues={selectedItem || {}}
            onSubmit={handleFormSubmit}
            onCancel={() => setFormOpen(false)}
            loading={loading}
            submitLabel={selectedItem ? 'Update' : 'Create'}
          />
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{config.singularName} Details</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <Tabs defaultValue="details">
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                {features.showActivityLog !== false && (
                  <TabsTrigger value="activity">
                    <History className="h-4 w-4 mr-2" />
                    Activity
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="details" className="mt-4">
                <div className="grid grid-cols-2 gap-4">
                  {config.formFields.map(field => (
                    <div key={field.name} className={field.gridSpan === 2 ? 'col-span-2' : ''}>
                      <label className="text-sm font-medium text-muted-foreground">
                        {field.label}
                      </label>
                      <p className="mt-1">
                        {formatDetailValue(selectedItem[field.name], field)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-6">
                  {canUpdate && (
                    <Button onClick={() => { setDetailOpen(false); handleEdit(selectedItem); }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setDetailOpen(false)}>
                    Close
                  </Button>
                </div>
              </TabsContent>
              {features.showActivityLog !== false && (
                <TabsContent value="activity" className="mt-4">
                  <EntityActivityLog
                    entityType={config.tableName}
                    entityId={selectedItem[config.keyField]}
                  />
                </TabsContent>
              )}
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {config.singularName}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {config.singularName.toLowerCase()}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedItems.size} {config.pluralName}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedItems.size} {config.pluralName.toLowerCase()}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDeleteConfirm} className="bg-destructive text-destructive-foreground">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatDetailValue(value: any, field: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (field.type === 'select' && field.options) {
    const option = field.options.find((o: any) => o.value === value);
    return option?.label || value;
  }
  return String(value);
}
